import { Profile } from "@/features/auth/types/auth-actions.type"
import { NonHomeownerUser } from "@/features/volunteers/types/volunteers.type"

/**
 * Maps the volunteer API response (GET /volunteer/user/:userId) to the Profile shape
 * used by the Profile page and ProfileAboutMeForm. This keeps Profile using the same
 * data source as Update Volunteer (volunteers table + user table via volunteer API).
 */
export function mapNonHomeownerUserToProfile(user: NonHomeownerUser): Profile {
    const v = (user as any).volunteer
    const servePeriods = v?.serveDates && Array.isArray(v.serveDates) && v.serveDates.length > 0
        ? v.serveDates
        : null
    const selectedRegions = (user as any).selectedRegions?.length
        ? (user as any).selectedRegions
        : user.selectedRegion != null
            ? [user.selectedRegion]
            : []

    return {
        id: user.id,
        createdAt: user.createdAt,
        updatedAt: user.updatedAt,
        fullname: user.fullname ?? "",
        email: user.email ?? "",
        email_verified: user.email_verified ?? false,
        organizationName: v?.organizationName ?? "",
        age: (user as any).age ?? 0,
        dateOfBirth: user.dateOfBirth ?? null,
        servingPreference: v?.servingPreference ?? null,
        groupNotes: v?.groupNotes ?? null,
        provider: (user as any).provider ?? "",
        profilePicture: user.profilePicture ?? null,
        availability: v?.availability ?? "",
        availabilityRange: null,
        servePeriods,
        experienceConstruction: v?.experienceConstruction ?? undefined,
        experienceManualLabor: v?.experienceManualLabor ?? undefined,
        password: null,
        experienceDataManagement: v?.experienceDataManagement ?? undefined,
        experienceStorytelling: v?.experienceStorytelling ?? undefined,
        mealPreparationAbility: v?.mealPreparationAbility != null ? String(v.mealPreparationAbility) : "",
        experienceLeadingTeams: v?.experienceLeadingTeams ?? undefined,
        experienceDrywallFinishing: v?.experienceDrywallFinishing,
        experienceRoughCarpentry: v?.experienceRoughCarpentry,
        experienceFinishCarpentry: v?.experienceFinishCarpentry,
        experienceFlooring: v?.experienceFlooring,
        experienceWoodWorking: v?.experienceWoodWorking,
        experienceElectrical: v?.experienceElectrical,
        experiencePlumbing: v?.experiencePlumbing,
        experienceHvac: v?.experienceHvac,
        experienceArborist: v?.experienceArborist,
        experienceRoofing: v?.experienceRoofing,
        experienceHeavyEquipment: v?.experienceHeavyEquipment,
        experienceAltMinistries: v?.experienceAltMinistries,
        skillsAndQualities: v?.skillsAndQualities ?? "",
        otherLanguages: v?.otherLanguages ?? "",
        hasServedBefore: (() => {
            const raw = v?.hasServedBefore
            if (raw == null || String(raw).trim() === '') return ""
            const s = String(raw).trim().toLowerCase()
            if (s === 'yes') return 'yes'
            if (s === 'no') return 'no'
            return ""
        })(),
        isChristFollower: (() => {
            const raw = v?.isChristFollower
            if (raw == null || String(raw).trim() === '') return ""
            const s = String(raw).trim().toLowerCase()
            if (s === 'yes') return 'yes'
            if (s === 'no') return 'no'
            return ""
        })(),
        preferredLocation: v?.preferredLocation ?? "",
        currentStep: user.currentStep ?? 1,
        isComplete: (user as any).isComplete ?? false,
        resetPasswordToken: (user as any).resetPasswordToken ?? null,
        resetPasswordExpires: (user as any).resetPasswordExpires ?? null,
        otp: (user as any).otp ?? null,
        otpExpires: (user as any).otpExpires ?? null,
        hoursWorked: v?.hoursWorked ?? 0,
        daysServed: v?.daysServed ?? 0,
        trainingVideosWatched: v?.trainingVideosWatched ?? 0,
        workScopesCompleted: v?.workScopesCompleted ?? 0,
        selectedRegion: user.selectedRegion ?? null,
        regionId: v?.regionId ?? user.selectedRegion ?? null,
        selectedRegions,
        expectations: v?.expectations ?? null,
        stmObjectives: v?.stmObjectives ?? null,
        devotions: v?.devotions ?? null,
        serveFeedback: v?.serveFeedback ?? null,
        vehicleMake: v?.vehicleMake ?? null,
        vehicleModel: v?.vehicleModel ?? null,
        vehicleColor: v?.vehicleColor ?? null,
        vehicleLicensePlateNumber: v?.vehicleLicensePlateNumber ?? null,
        vehicleLicensePlateState: v?.vehicleLicensePlateState ?? null,
        gender: v?.gender ?? null,
        tShirtSize: v?.tShirtSize ?? null,
        backgroundCheck: v?.backgroundCheck ?? null,
        passportNumber: v?.passportNumber ?? null,
        emergencyContact1FullName: v?.emergencyContact1FullName ?? null,
        emergencyContact1Relationship: v?.emergencyContact1Relationship ?? null,
        emergencyContact1Phone: v?.emergencyContact1Phone ?? null,
        emergencyContact2FullName: v?.emergencyContact2FullName ?? null,
        emergencyContact2Relationship: v?.emergencyContact2Relationship ?? null,
        emergencyContact2Phone: v?.emergencyContact2Phone ?? null,
        healthInsuranceProvider: v?.healthInsuranceProvider ?? null,
        healthInsurancePhone: v?.healthInsurancePhone ?? null,
        healthInsurancePolicyNumber: v?.healthInsurancePolicyNumber ?? null,
        travelInsuranceProvider: v?.travelInsuranceProvider ?? null,
        travelInsurancePhone: v?.travelInsurancePhone ?? null,
        travelInsurancePolicyNumber: v?.travelInsurancePolicyNumber ?? null,
        jamaicaC5ApplicationComplete: v?.jamaicaC5ApplicationComplete ?? false,
        jamaicaTripFlightDetails: v?.jamaicaTripFlightDetails ?? null,
        healthConditions: v?.healthConditions ?? null,
        dietaryRestrictions: v?.dietaryRestrictions ?? null,
        chatNotifications: user.chatNotifications ?? true,
        workOrderNotifications: user.workOrderNotifications ?? true,
        emailUpdates: user.emailUpdates ?? true,
        phone: user.phone ?? "",
        phone_verified: (user as any).phone_verified ?? false,
        isTwoFactorEnabled: (user as any).isTwoFactorEnabled ?? false,
        twoFactorCode: (user as any).twoFactorCode ?? null,
        twoFactorCodeExpires: (user as any).twoFactorCodeExpires ?? null,
        googleAccountLinked: (user as any).googleAccountLinked ?? null,
        prayerRequest: (v as any)?.prayerRequest ?? null,
    } as Profile
}
